<template>
  <div
    v-if="media"
    class="guided-mode__visual"
    :class="rootClasses"
    :aria-hidden="hasControls ? null : 'true'"
  >
    <img
      v-if="media && media.type !== 'animation' && !mediaFailed"
      :src="effectiveSrc"
      alt=""
      :aria-hidden="hasControls ? 'true' : null"
      @error="handleMediaError"
    >
    <video
      v-else-if="media && !mediaFailed"
      ref="video"
      :key="effectiveSrc"
      :autoplay="autoplayAllowed"
      loop
      :muted="!audioEnabled"
      playsinline
      preload="auto"
      :aria-hidden="hasControls ? 'true' : null"
      @loadedmetadata="applyPlaybackRate"
      @error="handleMediaError"
      @playing="onVideoPlaying"
    >
      <source :src="effectiveSrc" type="video/mp4">
    </video>
    <v-icon v-else color="#79b4bd" size="74">{{ fallbackIcon }}</v-icon>
    <button
      v-if="isToggle"
      type="button"
      class="guided-mode__visual-toggle"
      :aria-expanded="String(size === 'default')"
      :aria-label="toggleLabel"
      @click="$emit('toggle-size')"
    >
      <v-icon size="18">{{ size === 'default' ? mdiArrowCollapse : mdiChevronDown }}</v-icon>
    </button>
    <button
      v-if="hasControls && hasAudioTrack"
      type="button"
      class="guided-mode__visual-sound"
      :aria-pressed="String(audioEnabled)"
      :aria-label="audioLabel"
      @click.stop="$emit('toggle-audio')"
    >
      <v-icon size="20">{{ audioEnabled ? mdiVolumeHigh : mdiVolumeOff }}</v-icon>
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mapState } from 'pinia'
import { mdiArrowCollapse, mdiChevronDown, mdiVolumeHigh, mdiVolumeOff } from '@mdi/js'
import { usePartnerConfigStore } from '@/stores/partnerConfig'
import { videoHasAudioTrack } from '../utils/media-audio'
import type { GuidedModeMedia } from '../types/guided-mode.types'
import type { GuidedMediaSize } from '../utils/media-presentation'

export default defineComponent({
  name: 'GuidedModeVisual',
  props: {
    media: {
      type: Object as PropType<GuidedModeMedia | null>,
      default: null,
    },
    fallbackIcon: {
      type: String,
      required: true,
    },
    playbackRate: {
      type: Number,
      default: 1,
    },
    size: {
      type: String as PropType<GuidedMediaSize>,
      default: 'default',
    },
    interactive: {
      type: Boolean,
      default: false,
    },
    audioControl: {
      type: Boolean,
      default: false,
    },
    toggleLabel: {
      type: String,
      default: '',
    },
    audioEnabled: {
      type: Boolean,
      default: false,
    },
    audioLabel: {
      type: String,
      default: '',
    },
  },
  emits: ['toggle-size', 'toggle-audio'],
  data: () => ({
    mediaFailed: false,
    cachedSrc: null as string | null,
    cacheRequestId: 0,
    detectedAudioTrack: false,
    mdiArrowCollapse,
    mdiChevronDown,
    mdiVolumeHigh,
    mdiVolumeOff,
  }),
  computed: {
    ...mapState(usePartnerConfigStore, ['skinTone']),
    backgroundClass(): string {
      return this.skinTone === 'black' ? 'guided-mode__visual--black' : ''
    },
    hasControls(): boolean {
      return this.interactive || this.audioControl
    },
    isToggle(): boolean {
      return this.interactive && Boolean(this.media)
    },
    hasAudioTrack(): boolean {
      return this.media?.hasAudio === true || this.detectedAudioTrack
    },
    autoplayAllowed(): boolean {
      return !this.interactive || !this.hasAudioTrack
    },
    rootClasses(): (string | Record<string, boolean>)[] {
      return [
        this.backgroundClass,
        { 'guided-mode__visual--thumbnail': this.size === 'thumbnail' },
      ]
    },
    src(): string {
      if (!this.media) return ''
      if (this.media.type === 'gif') return `static/images/shared/gif/${this.media.asset}.gif`
      const directory = this.media.type === 'animation' ? 'animated' : 'banner'
      const extension = this.media.type === 'animation' ? 'mp4' : 'jpg'
      return `static/images/default/img/${directory}/${this.skinTone || 'caucasian'}/${this.media.asset}.${extension}`
    },
    effectiveSrc(): string {
      return this.cachedSrc || this.src
    },
  },
  watch: {
    src: {
      immediate: true,
      handler() {
        this.mediaFailed = false
        this.detectedAudioTrack = false
        this.resolveCachedSrc()
      },
    },
    playbackRate: 'applyPlaybackRate',
    audioEnabled: 'syncAudioPlayback',
  },
  beforeDestroy() {
    this.clearCachedSrc()
  },
  methods: {
    applyPlaybackRate(): void {
      const video = this.$refs.video as unknown as HTMLVideoElement | undefined
      if (video) video.playbackRate = this.playbackRate
    },
    onVideoPlaying(): void {
      if (!this.detectedAudioTrack && videoHasAudioTrack(this.$refs.video as unknown as HTMLVideoElement)) {
        this.detectedAudioTrack = true
      }
    },
    clearCachedSrc(): void {
      if (this.cachedSrc) URL.revokeObjectURL(this.cachedSrc)
      this.cachedSrc = null
    },
    async resolveCachedSrc(): Promise<boolean> {
      const requestId = ++this.cacheRequestId
      this.clearCachedSrc()
      if (!this.src || typeof caches === 'undefined') return false

      try {
        const assetUrl = new URL(this.src, window.location.href).href
        const response = await caches.match(assetUrl)
        if (!response) return false

        const objectUrl = URL.createObjectURL(await response.blob())
        if (requestId !== this.cacheRequestId) {
          URL.revokeObjectURL(objectUrl)
          return false
        }
        this.cachedSrc = objectUrl
        return true
      } catch (error) {
        return false
      }
    },
    async handleMediaError(): Promise<void> {
      if (!this.cachedSrc && await this.resolveCachedSrc()) return
      this.mediaFailed = true
    },
    syncAudioPlayback(): void {
      const video = this.$refs.video as unknown as HTMLVideoElement | undefined
      if (!video) return
      if (this.audioEnabled) video.play()?.catch(() => {})
      else video.pause()
    },
  },
})
</script>
